import { manifestSchema, mallFileSchema, shopFileSchema, type DialogueAction, type Manifest } from "./schema.js";

export interface MergeOptions {
  /** Keep archived shops/locations (for linting the full, restorable content). Default false. */
  includeArchived?: boolean;
}

/** Parse a mall file plus its shop files (already read as JSON) into one validated manifest. */
export function mergeManifest(mallJson: unknown, shopJsons: unknown[], options: MergeOptions = {}): Manifest {
  const mall = mallFileSchema.parse(mallJson);
  const shops = shopJsons.map((j) => shopFileSchema.parse(j));
  const full = manifestSchema.parse({
    schemaVersion: 1,
    mall: mall.mall,
    districts: mall.districts,
    shops: shops.map((s) => s.shop),
    locations: [...mall.locations, ...shops.flatMap((s) => s.locations)],
    transitions: [...mall.transitions, ...shops.flatMap((s) => s.transitions)],
    characters: [...mall.characters, ...shops.flatMap((s) => s.characters)],
    products: shops.flatMap((s) => s.products)
  });
  return options.includeArchived ? full : applyArchive(full, mall.archived.locations, mall.archived.shops);
}

/** Strip archived shops/locations and everything that references them (soft delete). */
export function applyArchive(m: Manifest, locationIds: string[], shopIds: string[]): Manifest {
  const shopGone = new Set(shopIds);
  if (locationIds.length === 0 && shopGone.size === 0) return m;
  const locGone = new Set(locationIds);
  for (const l of m.locations) if (l.shopId && shopGone.has(l.shopId)) locGone.add(l.id);
  const products = m.products.filter((p) => !shopGone.has(p.shopId));
  const productIds = new Set(products.map((p) => p.id));
  const actionOk = (a?: DialogueAction): boolean => {
    if (!a) return true;
    if (a.type === "goToLocation") return !locGone.has(a.locationId);
    if (a.type === "goToShop") return !shopGone.has(a.shopId);
    if (a.type === "showProduct" || a.type === "addToBasket") return productIds.has(a.productId);
    return true;
  };
  const characters = m.characters
    .filter((c) => !locGone.has(c.locationId) && !(c.shopId && shopGone.has(c.shopId)))
    .map((c) => ({
      ...c,
      script: {
        ...c.script,
        nodes: Object.fromEntries(
          Object.entries(c.script.nodes).map(([k, n]) => [k, { ...n, say: n.sayTrimmed ?? n.say, options: n.options.filter((o) => actionOk(o.action)) }])
        )
      }
    }));
  const archivedNames = [...m.shops.filter((s) => shopGone.has(s.id)).map((s) => s.name), ...m.locations.filter((l) => locGone.has(l.id)).map((l) => l.name)];
  return {
    ...m,
    archivedNames,
    shops: m.shops.filter((s) => !shopGone.has(s.id)),
    locations: m.locations
      .filter((l) => !locGone.has(l.id))
      .map((l) => ({
        ...l,
        hotspots: l.hotspots
          .map((h) => (h.kind === "menu" ? { ...h, items: h.items.filter((i) => !locGone.has(i.to)) } : h))
          .filter(
            (h) =>
              !(h.kind === "navigate" && locGone.has(h.to)) &&
              !(h.kind === "product" && !productIds.has(h.productId)) &&
              !(h.kind === "menu" && h.items.length === 0)
          )
      })),
    transitions: m.transitions.filter((t) => !locGone.has(t.from) && !locGone.has(t.to)),
    characters,
    products
  };
}

/** Browser/Node loader: `read(path)` resolves a path relative to the content root to parsed JSON. */
export async function loadManifest(read: (path: string) => Promise<unknown>, options: MergeOptions = {}): Promise<Manifest> {
  const mallJson = await read("mall.json");
  const mall = mallFileSchema.parse(mallJson);
  const shopJsons = await Promise.all(mall.shopFiles.map((f) => read(f)));
  return mergeManifest(mallJson, shopJsons, options);
}
